'use client'

import { type ISbStoryData } from '@storyblok/react/rsc'
import { type ReadonlyURLSearchParams, useSearchParams } from 'next/navigation'
import { type FC } from 'react'
import { type IntlShape, useIntl } from 'react-intl'

import { apmCaptureError } from '../../../apm'
import {
  SEO_META_DEDUPE_KEY_ROBOTS,
  SeoHeadDescriptionMetas,
  SeoHeadTitleMetas,
} from '../../../seo'
import { useStoryblokStory } from '../../context/StoryblokStoryContext'
import { type CmsAlternateUrlsMetadata } from '../../model/storyAccessors/CmsAlternateUrlsMetadata'
import { storyAccessorGetCanonicalUrl } from '../../model/storyAccessors/storyAccessorGetCanonicalUrl'
import { storyAccessorGetSeoRobotsValueOrDefault } from '../../model/storyAccessors/storyAccessorGetSeoRobotsValueOrDefault'
import { type ContentType } from '../../storyblok'

import { CmsSeoHrefLangs } from './CmsSeoHrefLangs'

type Props = {
  hrefLangs: CmsAlternateUrlsMetadata[]
}

type MetaTypeFields = {
  metaDescription?: string
  metaTitle?: string
}

const getStoryblokBrandPageMetaFields = ({
  intl,
  story,
}: {
  intl: IntlShape
  story: ISbStoryData<ContentType>
}): MetaTypeFields => {
  const { seoDescription, seoTitle, title } = story.content

  return {
    metaDescription:
      typeof seoDescription === 'string' && seoDescription
        ? seoDescription
        : intl.formatMessage(
            { id: 'brandPage.meta.description.fallback' },
            { name: title },
          ),
    metaTitle:
      typeof seoTitle === 'string' && seoTitle
        ? seoTitle
        : intl.formatMessage(
            { id: 'brandPage.meta.title.fallback' },
            { name: title },
          ),
  }
}

const getStoryblokCategoryPageMetaFields = ({
  intl,
  story,
}: {
  intl: IntlShape
  story: ISbStoryData<ContentType>
}): MetaTypeFields => {
  const { seoDescription, seoTitle, title } = story.content

  return {
    metaDescription:
      typeof seoDescription === 'string' && seoDescription
        ? seoDescription
        : intl.formatMessage(
            { id: 'categoryPage.meta.description.fallback' },
            { name: title },
          ),
    metaTitle:
      typeof seoTitle === 'string' && seoTitle
        ? seoTitle
        : intl.formatMessage(
            { id: 'categoryPage.meta.title.fallback' },
            { name: title },
          ),
  }
}

const getStoryblokPageMetaFieldsByPageType = ({
  intl,
  story,
}: {
  intl: IntlShape
  story: ISbStoryData<ContentType>
}): MetaTypeFields => {
  const { component, seoDescription, seoTitle, title } = story.content

  switch (component) {
    case 'AkeneoCategoryPage': {
      return getStoryblokCategoryPageMetaFields({
        intl,
        story,
      })
    }
    case 'BrandPage': {
      return getStoryblokBrandPageMetaFields({
        intl,
        story,
      })
    }
    default: {
      const metaTitle =
        typeof seoTitle === 'string' && seoTitle ? seoTitle : title
      const metaDescription =
        typeof seoDescription === 'string' && seoDescription
          ? seoDescription
          : undefined

      return { metaDescription, metaTitle }
    }
  }
}

const getPageNumberFromSearch = (
  searchParams: ReadonlyURLSearchParams,
): number | undefined => {
  const rawPageNumber = searchParams.get('pageNumber')

  return rawPageNumber ? Math.trunc(Number(rawPageNumber)) : undefined
}

const getStoryblokCanonicalUrlWithPageNumber = ({
  searchParams,
  story,
}: {
  searchParams: ReadonlyURLSearchParams
  story: ISbStoryData<ContentType>
}): string => {
  const canonicalUrl = storyAccessorGetCanonicalUrl(story)
  const parsedPageNumber = getPageNumberFromSearch(searchParams)

  const isPageNumberValid =
    typeof parsedPageNumber === 'number' &&
    Number.isSafeInteger(parsedPageNumber) &&
    parsedPageNumber > 1

  if (!isPageNumberValid) {
    return canonicalUrl
  }

  try {
    const canonicalUrlObject = new URL(canonicalUrl)
    canonicalUrlObject.searchParams.set(
      'pageNumber',
      parsedPageNumber.toString(),
    )

    return canonicalUrlObject.href
  } catch (error) {
    apmCaptureError(
      new Error('Failed to parse storyblok canonical url', {
        cause: error,
      }),
    )

    return canonicalUrl
  }
}

export const CmsSeo: FC<Props> = ({ hrefLangs }) => {
  const intl = useIntl()
  const { story } = useStoryblokStory()
  const searchParams = useSearchParams()

  const canonicalUrlWithPageNumber = getStoryblokCanonicalUrlWithPageNumber({
    searchParams,
    story,
  })

  const robotsValue = storyAccessorGetSeoRobotsValueOrDefault(story)

  const { metaDescription, metaTitle } = getStoryblokPageMetaFieldsByPageType({
    intl,
    story,
  })

  return (
    <>
      {metaTitle ? <SeoHeadTitleMetas title={metaTitle} /> : null}
      {metaDescription ? (
        <SeoHeadDescriptionMetas description={metaDescription} />
      ) : null}
      <link href={canonicalUrlWithPageNumber} rel="canonical" />
      <CmsSeoHrefLangs hrefLangs={hrefLangs} story={story} />
      {metaTitle ? <meta content={metaTitle} name="name" /> : null}
      <meta
        content={robotsValue}
        // key provided to dedupe/override one in global seo config WSAWA-6195
        key={SEO_META_DEDUPE_KEY_ROBOTS}
        name="robots"
      />
      <meta content={canonicalUrlWithPageNumber} property="og:url" />
      <meta content="website" property="og:type" />
      <meta content="summary" name="twitter:card" />
    </>
  )
}
